// ============================================
// Path: /frontend/src/pages/SearchPage.jsx
// Purpose: Search results page with dynamic filtering for listings
// UPDATED: Mobile-first responsive styling applied
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ListingCard } from '../components/ListingCard';
import { Skeleton } from '../components/ui/skeleton';
import { listingsAPI } from '../api/listings.api';
import { bookmarksAPI } from '../api/bookmarks.api';
import { useAuth } from '../hooks/useAuth';
import QuickFiltersBar from '../components/listings/QuickFiltersBar';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton className="aspect-[4/3] w-full" />
        <div className="space-y-1.5 sm:space-y-2 px-2">
          <Skeleton className="h-3 sm:h-4 w-1/4" />
          <Skeleton className="h-4 sm:h-5 w-3/4" />
          <Skeleton className="h-4 sm:h-5 w-1/2" />
          <Skeleton className="h-3 sm:h-4 w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function SearchPage() {
  console.log('🎨 [SearchPage] Component mounted');

  // ========================================
  // HOOKS & STATE
  // ========================================
  const [searchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quickFilter, setQuickFilter] = useState(null);
  const [bookmarkedIds, setBookmarkedIds] = useState([]);

  // Extract search parameters
  const qParam = searchParams.get('q');
  const categoryParam = searchParams.get('category');
  const countyParam = searchParams.get('county');
  const subCountyParam = searchParams.get('subCounty');
  const typeParam = searchParams.get('type');
  const agentIdParam = searchParams.get('agentId');
  const makeParam = searchParams.get('make');
  const modelParam = searchParams.get('model');
  const { user } = useAuth();
  
  console.log('🔍 [SearchPage] Search params:', {
    q: qParam,
    category: categoryParam,
    county: countyParam,
    subCounty: subCountyParam,
    type: typeParam,
    agentId: agentIdParam,
    make: makeParam,
    model: modelParam
  });

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getSearchDescription = () => {
    const parts = [];
    if (qParam) parts.push(`keyword "${qParam}"`);
    if (typeParam) parts.push(`type "${typeParam}"`);
    if (subCountyParam) parts.push(`location "${subCountyParam}"`);
    else if (countyParam) parts.push(`location "${countyParam}"`);
    if (makeParam) parts.push(`make "${makeParam}"`);
    if (modelParam) parts.push(`model "${modelParam}"`);
    if (agentIdParam) parts.push(`a specific agent`);
    
    if (parts.length === 0) return "Showing all listings.";
    return `Showing results for: ${parts.join(', ')}`;
  };

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchSearchResults = async () => {
      console.log('🔵 [SearchPage] Fetching search results...');
      setLoading(true);
      
      try {
        // Build query parameters object
        const queryParams = {
          status: 'APPROVED'
        };

        if (qParam) queryParams.search = qParam;
        if (categoryParam) queryParams.category = categoryParam.toUpperCase();
        if (countyParam) queryParams.county = countyParam;
        if (subCountyParam) queryParams.subCounty = subCountyParam;
        if (typeParam) queryParams.type = typeParam;
        if (agentIdParam) queryParams.userId = agentIdParam;
        if (makeParam) queryParams.make = makeParam;
        if (modelParam) queryParams.model = modelParam;

         // Apply quick filters
        if (quickFilter?.where) {
          Object.entries(quickFilter.where).forEach(([key, value]) => {
            if (key === 'user') {
              // Handle nested user filters
              if (value.role) queryParams.userRole = value.role;
              if (value.subscriptionTier?.in) queryParams.subscriptionTiers = value.subscriptionTier.in.join(',');
            } else if (key === 'price') {
              if (value.lte) queryParams.maxPrice = value.lte;
            } else if (key === 'createdAt') {
              if (value.gte) queryParams.createdAfter = value.gte;
            } else {
              queryParams[key] = value;
            }
          });
        }

        // Apply quick filter ordering
        if (quickFilter?.orderBy) {
          const [field, direction] = Object.entries(quickFilter.orderBy)[0];
          queryParams.sortBy = field;
          queryParams.sortOrder = direction;
        }

        console.log('🔵 [SearchPage] Query params:', queryParams);

        const data = await listingsAPI.getListings(queryParams);
        console.log('✅ [SearchPage] Search results received:', data);
        setListings(data.listings || []);
        setError(null);

        // Batch check bookmarks if user is logged in
        if (user && data.listings?.length > 0) {
          const listingIds = data.listings.map(l => l.id);
          const bookmarkData = await bookmarksAPI.checkBookmarksBatch(listingIds);
          setBookmarkedIds(bookmarkData.bookmarked || []);
          console.log('📌 [SearchPage] Bookmarked IDs:', bookmarkData.bookmarked?.length || 0);
        }
      } catch (err) {

        console.error('❌ [SearchPage] Search error:', err);
        setError(err.response?.data?.message || 'Failed to load search results');
      } finally {
        setLoading(false);
      }
    };

    fetchSearchResults();
  }, [qParam, categoryParam, countyParam, subCountyParam, typeParam, agentIdParam, makeParam, modelParam, quickFilter]);

  // ========================================
  // RENDER CONTENT
  // ========================================
  const renderContent = () => {
    if (loading) {
      console.log('⏳ [SearchPage] Loading...');
      return <LoadingSkeleton />;
    }

    if (error) {
      console.log('🔴 [SearchPage] Error state:', error);
      return (
        <p className="text-center text-destructive py-8 sm:py-12 text-sm sm:text-base">
          Error loading search results: {error}
        </p>
      );
    }

    if (listings.length === 0) {
      console.log('📭 [SearchPage] No results found');
      return (
        <p className="text-center text-muted-foreground py-8 sm:py-12 text-sm sm:text-base px-4">
          No listings found matching your criteria. Try broadening your search.
        </p>
      );
    }

    console.log('🎨 [SearchPage] Rendering listings:', listings.length);
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 md:gap-4">
        {listings.map((listing) => (
         <ListingCard 
          key={listing.id} 
          id={listing.id}
          title={listing.title}
          price={listing.price}
          location={`${listing.subCounty}, ${listing.county}`}
          imageUrl={listing.imageUrl}
          type={listing.subType || listing.type}
          category={listing.category}
          createdAt={listing.createdAt}
          allowGroupBuy={listing.allowGroupBuy}
          chamaInterestCount={listing._count?.chamaInterests || 0}
          initialBookmarked={bookmarkedIds.includes(listing.id)}
        />
        ))}
      </div>
    );
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <div className="text-left mb-4 sm:mb-6 md:mb-8">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-1 sm:mb-2">Search Results</h1>
          <p className="text-xs sm:text-sm md:text-base text-muted-foreground">{getSearchDescription()}</p>
        </div>
        <div className="mb-4 sm:mb-6">
          <QuickFiltersBar 
            category={categoryParam?.toUpperCase() || 'VEHICLE'} 
            onFilterApply={setQuickFilter}
          />
        </div>
        {renderContent()}
      </main>
      <Footer />
    </div>
  );
}